import { useCallback, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Drop-in replacement for react-to-print's useReactToPrint hook.
 * Generates and downloads a real PDF file (no browser print dialog).
 *
 * Usage stays identical:
 *   const handlePrint = useElementToPdf({ contentRef, documentTitle, pageStyle });
 *   <Button onClick={handlePrint}>...</Button>
 */

const parsePageStyle = (pageStyle = '') => {
  const cfg = { format: 'a4', orientation: 'portrait', margin: 10, customWidthMm: null };
  const sizeMatch = pageStyle.match(/@page[^{]*\{[^}]*size\s*:\s*([^;}]+)/i);
  if (sizeMatch) {
    const size = sizeMatch[1].toLowerCase();
    if (size.includes('landscape')) cfg.orientation = 'landscape';
    if (size.includes('a3')) cfg.format = 'a3';
    else if (size.includes('letter')) cfg.format = 'letter';
    else if (size.includes('a4')) cfg.format = 'a4';
    const widthMatch = size.match(/(\d+(?:\.\d+)?)\s*mm/);
    if (widthMatch && !size.includes('a4') && !size.includes('a3') && !size.includes('letter')) {
      cfg.customWidthMm = parseFloat(widthMatch[1]);
    }
  }
  const marginMatch = pageStyle.match(/@page[^{]*\{[^}]*margin\s*:\s*(\d+(?:\.\d+)?)\s*(?:mm)?\b/i);
  if (marginMatch) cfg.margin = parseFloat(marginMatch[1]);
  return cfg;
};

const sanitizeFilename = (name) => {
  if (!name) return 'document';
  // Strip extension if user already added .pdf
  return String(name).replace(/\.pdf$/i, '').slice(0, 120);
};

export default function useElementToPdf({ contentRef, documentTitle = 'document', pageStyle = '', onBeforeGetContent, onAfterPrint } = {}) {
  const busy = useRef(false);

  return useCallback(async () => {
    if (busy.current) return;
    const element = contentRef?.current;
    if (!element) {
      toast.error('لا يوجد محتوى للتنزيل');
      return;
    }

    busy.current = true;
    let toastId;
    let wrapper = null;
    try {
      if (typeof onBeforeGetContent === 'function') {
        await onBeforeGetContent();
      }
      toastId = toast.loading('جاري إنشاء ملف PDF...');

      // Clone the target into a visible off-screen wrapper so html2canvas can capture
      // it even if it currently lives inside a `display: none` / `hidden` ancestor.
      const clone = element.cloneNode(true);
      wrapper = document.createElement('div');
      wrapper.setAttribute('aria-hidden', 'true');
      wrapper.style.cssText = [
        'position: fixed',
        'left: 0',
        'top: 0',
        'z-index: -1',
        'opacity: 0',
        'pointer-events: none',
        'background: #fff',
      ].join(';');
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      const cfg = parsePageStyle(pageStyle);
      const html2pdf = (await import('html2pdf.js')).default;

      // Determine jsPDF format: A4/A3/Letter or custom narrow (e.g. 80mm thermal receipt)
      const jsPDFOpts = cfg.customWidthMm
        ? { unit: 'mm', format: [cfg.customWidthMm, 297], orientation: 'portrait' }
        : { unit: 'mm', format: cfg.format, orientation: cfg.orientation };

      const filename = `${sanitizeFilename(documentTitle)}.pdf`;

      await html2pdf()
        .set({
          margin: cfg.margin,
          filename,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            logging: false,
            windowWidth: clone.scrollWidth || undefined,
          },
          jsPDF: jsPDFOpts,
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(clone)
        .save();

      toast.success('تم تحميل ملف PDF', { id: toastId });
      if (typeof onAfterPrint === 'function') {
        try { await onAfterPrint(); } catch (err) {
          console.debug('[useElementToPdf] onAfterPrint callback failed (PDF was saved successfully):', err);
        }
      }
    } catch (err) {
      console.error('PDF generation failed', err);
      toast.error('فشل إنشاء ملف PDF', { id: toastId });
    } finally {
      if (wrapper && wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      }
      busy.current = false;
    }
  }, [contentRef, documentTitle, pageStyle, onBeforeGetContent, onAfterPrint]);
}
